import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import ClientAuthContext from "../../context/ClientAuthContext";
import { serverInstance } from "../../service/axios";
import { resolveSafeReturnPath } from "../../utils/resolveSafeReturnPath";
import "./sign.scss";

const SignIn = ({ data, language }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userData, setUserData } = useContext(ClientAuthContext);
  const [searchParams] = useSearchParams();
  const defaultReturnPath = useMemo(
    () => resolveSafeReturnPath(location.state, searchParams),
    [location.state, searchParams]
  );
  const [loginMethod, setLoginMethod] = useState("email"); // "email" or "sms"
  const [smsStep, setSmsStep] = useState("phone"); // "phone" or "otp"
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    phoneNumber: "",
    otpCode: "",
  });
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [checked, setChecked] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const [cooldownTimer, setCooldownTimer] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("msidclienttoken");
    if (token && userData?.id) {
      navigate(defaultReturnPath, { replace: true });
    }
  }, [defaultReturnPath, navigate, userData?.id]);

  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setFormData((prev) => ({ ...prev, email: savedEmail }));
      setChecked(true);
    }
  }, []);

  // OTP Timer effect
  useEffect(() => {
    let interval = null;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((timer) => timer - 1);
      }, 1000);
    } else if (otpTimer === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Cooldown Timer effect
  useEffect(() => {
    let interval = null;
    if (cooldownTimer > 0) {
      interval = setInterval(() => {
        setCooldownTimer((timer) => timer - 1);
      }, 1000);
    } else if (cooldownTimer === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [cooldownTimer]);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setErrorMsg("");
    setSuccessMsg("");
  };

  const validatePhoneNumber = (phone) => {
    const phoneRegex = /^[0-9]{8}$/;
    return phoneRegex.test(phone);
  };

  const validateOTP = (otp) => {
    const otpRegex = /^[0-9]{6}$/;
    return otpRegex.test(otp);
  };

  const establishSession = async (token) => {
    const normalizedToken = typeof token === "string" ? token.trim() : "";
    if (!normalizedToken) {
      throw new Error("Missing authentication token");
    }

    localStorage.setItem("msidclienttoken", normalizedToken);

    try {
      const profileResponse = await serverInstance.post(
        "/client",
        {},
        {
          headers: {
            Authorization: `Bearer ${normalizedToken}`,
            "Content-Type": "application/json",
          },
        }
      );
      const profile = profileResponse?.data?.data;

      if (!profile?.id) {
        throw new Error("Invalid client profile");
      }

      setUserData(profile);
      navigate(defaultReturnPath, { replace: true });
    } catch (error) {
      if (localStorage.getItem("msidclienttoken") === normalizedToken) {
        localStorage.removeItem("msidclienttoken");
      }
      setUserData({});
      throw error;
    }
  };

  const sendOTP = async () => {
    if (!validatePhoneNumber(formData.phoneNumber)) {
      setErrorMsg(data.invalidPhoneNumber);
      return;
    }

    if (cooldownTimer > 0) {
      setErrorMsg(data.otpAlreadySent);
      return;
    }

    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");
    
    try {
      // First check if mobile number is registered
      const registrationCheck = await serverInstance.post("/client/mobile-registered", {
        mobile_number: formData.phoneNumber
      });
      
      if (registrationCheck.data.message === "NOT_REGISTERED") {
        setErrorMsg(data.mobileNotRegistered);
        setLoading(false);
        return;
      }
      
      // If registered, proceed to send OTP
      await serverInstance.post("/sms/send", {
        mobile_number: formData.phoneNumber,
        lang: language ? "en" : "mn" // true = English, false = Mongolian
      });
      setSmsStep("otp"); // Move to OTP step
      setOtpTimer(60); // 60 seconds timer for resend
      setCooldownTimer(60); // 60 seconds cooldown timer
      setSuccessMsg(data.otpSentTo.replace('{phoneNumber}', formData.phoneNumber));
      // Clear any previous OTP code when sending new OTP
      setFormData(prev => ({ ...prev, otpCode: "" }));
    } catch (error) {
      console.error("Send OTP Error:", error);
      
      // Handle specific error cases from the API
      const errorMessage = error?.response?.data?.message;
      
      if (errorMessage === "OTP_ALREADY_SENT") {
        setErrorMsg(data.otpAlreadySent);
        setCooldownTimer(60); // Set cooldown timer even on error
      } else {
        setErrorMsg(errorMessage || "Failed to send OTP");
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyOTP = async () => {
    if (!validateOTP(formData.otpCode)) {
      setErrorMsg(data.invalidOTP);
      return;
    }

    setLoading(true);
    setErrorMsg("");

    try {
      const response = await serverInstance.post("/client/signin/mobile", {
        mobile_number: formData.phoneNumber,
        code: formData.otpCode
      });
      await establishSession(response.data.token);
    } catch (error) {
      console.error("Verify OTP Error:", error);
      
      // Handle specific error cases from the API
      const errorMessage = error?.response?.data?.message;
      
      if (errorMessage === "OTP_EXPIRED") {
        setErrorMsg(data.otpExpired);
        setSmsStep("phone"); // Go back to phone step
        setOtpTimer(0); // Reset timer
      } else if (errorMessage === "NOT_FOUND") {
        setErrorMsg(data.otpIncorrect);
      } else if (errorMessage === "OTP_INCORRECT") {
        setErrorMsg(data.otpIncorrect);
      } else if (errorMessage === "OTP_NOT_FOUND") {
        setErrorMsg(data.otpNotFound);
        setSmsStep("phone"); // Go back to phone step
        setOtpTimer(0); // Reset timer
      } else {
        setErrorMsg(errorMessage || "Invalid OTP code");
      }
    } finally {
      setLoading(false);
    }
  };

  const goBackToPhone = () => {
    setSmsStep("phone");
    setOtpTimer(0);
    setCooldownTimer(0);
    setErrorMsg("");
    setSuccessMsg("");
    setFormData(prev => ({ ...prev, otpCode: "" }));
  };

  const loginWithEmail = async (e) => {
    e.preventDefault();
    
    if (loginMethod === "email") {
      if (checked) {
        localStorage.setItem("rememberedEmail", formData.email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      setLoading(true);
      try {
        const response = await serverInstance.post("/client/signin", {
          email: formData.email.toLowerCase(),
          password: formData.password
        });
        await establishSession(response.data.token);
      } catch (error) {
        console.error("SignIn Error:", error);
        setErrorMsg(error?.response?.data?.message || data.error);
      } finally {
        setLoading(false);
      }
    } else {
      // SMS OTP flow
      if (smsStep === "phone") {
        sendOTP();
      } else {
        verifyOTP();
      }
    }
  };

  return (
    <div className="sign-container">
      <div className="sign-container-card">
        {/* {loading ? <Dimmer /> : null} */}
        <h2>{data.signin}</h2>

        {/* Login Method Toggle */}
        <div className="form-div">
          <div className="login-method-toggle">
            <button
              type="button"
              className={`toggle-btn ${loginMethod === "email" ? "active" : ""}`}
              onClick={() => {
                setLoginMethod("email");
                setSmsStep("phone");
                setErrorMsg("");
                setSuccessMsg("");
              }}
            >
              {data.loginWithEmail}
            </button>
            <button
              type="button"
              className={`toggle-btn ${loginMethod === "sms" ? "active" : ""}`}
              onClick={() => {
                setLoginMethod("sms");
                setSmsStep("phone");
                setErrorMsg("");
                setSuccessMsg("");
              }}
            >
              {data.loginWithSMS}
            </button>
          </div>
        </div>

        <form className="sign-form" onSubmit={loginWithEmail}>
          {errorMsg ? (
            <div className="form-div error-msg">
              {/* <PiWarningCircleLight /> */}
              &nbsp;{errorMsg}
            </div>
          ) : null}
          
          {successMsg ? (
            <div className="form-div success-msg">
              {successMsg}
            </div>
          ) : null}

          {loginMethod === "email" ? (
            <>
              <div className="form-div">
                <label className="sign-label">{data.email}</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoCapitalize="none"
                  className="sign-input"
                />
              </div>
              <div className="form-div">
                <label className="sign-label">{data.password}</label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="sign-input"
                />
              </div>
            </>
          ) : (
            <>
              {smsStep === "phone" ? (
                <div className="form-div">
                  <label className="sign-label">{data.phoneNumber}</label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    required
                    className="sign-input"
                    maxLength={8}
                  />
                </div>
              ) : (
                <>
                  <div className="form-div">
                    <label className="sign-label">{data.otpCode}</label>
                    <input
                      type="text"
                      name="otpCode"
                      value={formData.otpCode}
                      onChange={handleChange}
                      required
                      className="sign-input"
                      maxLength={6}
                    />
                  </div>
                </>
              )}
            </>
          )}

          <div className="form-div">
            {loginMethod === "email" ? (
              <div className="flexrow">
                <label className="custom-checkbox">
                  <input
                    type="checkbox"
                    onChange={() => setChecked(!checked)}
                    checked={checked}
                  />
                  <span className="box" />
                  <span style={{ fontWeight: checked ? "bold" : "normal" }}>
                    {data.rememberUsername}
                  </span>
                </label>
                <Link to={"/forgot-password"} className="sign-text forgot">
                  {data.forgotPassword}
                </Link>
              </div>
            ) : (
              <div className="flexrow">
                {smsStep === "otp" && (
                  <button
                    type="button"
                    className="back-btn"
                    onClick={goBackToPhone}
                    disabled={loading}
                  >
                    {data.backToPhone}
                  </button>
                )}
                {smsStep === "otp" && otpTimer > 0 && (
                  <span className="otp-timer">
                    Resend in {otpTimer}s
                  </span>
                )}
                {smsStep === "otp" && otpTimer === 0 && cooldownTimer === 0 && (
                  <button
                    type="button"
                    className="resend-btn"
                    onClick={sendOTP}
                    disabled={loading}
                  >
                    {data.resendOTP}
                  </button>
                )}
                {smsStep === "phone" && cooldownTimer > 0 && (
                  <span className="cooldown-timer">
                    {data.waitForCooldown.replace('{seconds}', cooldownTimer)}
                  </span>
                )}
              </div>
            )}
            
            <button 
              type="submit" 
              className="sign-button" 
              disabled={loading || (loginMethod === "sms" && smsStep === "phone" && cooldownTimer > 0)}
            >
              {loginMethod === "email" 
                ? data.signin 
                : smsStep === "otp"
                  ? data.verifyOTP 
                  : cooldownTimer > 0
                    ? `Wait ${cooldownTimer}s`
                    : data.sendOTP
              }
            </button>
          </div>
          
          <div className="flexrow">
            <div
              style={{
                border: "1px solid gray",
                height: "1px",
                width: "20%",
              }}
            />
            <p className="gray-text">{data.textOne}</p>
            <div
              style={{
                border: "1px solid gray",
                height: "1px",
                width: "20%",
              }}
            />
          </div>
          <div className="form-div signup-link">
            <Link to={"/signup"} className="sign-text">
              <button className="sign-button2" type="submit">
                {data.textTwo}
              </button>
            </Link>
          </div>
          <div className="form-div signup-link"></div>
        </form>
      </div>
    </div>
  );
};

export default SignIn;
